import { useEffect } from 'react';
import { Link, NavLink, Outlet, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Icon } from '@/components/icon/index.js';
import type { IconName } from '@/components/icon/index.js';
import { cn } from '../lib/utils.js';
import { useAuthStore } from '../store/authStore.js';

type CompanyNavItem = {
  to: string;
  end?: boolean;
  iconName: IconName;
  labelKey: string;
  defaultLabel: string;
};

const NAV_ITEMS: CompanyNavItem[] = [
  { to: '/company', end: true, iconName: 'bar-chart-3', labelKey: 'company.tabs.dashboard', defaultLabel: 'Dashboard' },
  { to: '/company/simulations', iconName: 'file-text', labelKey: 'company.tabs.simulations', defaultLabel: 'Simulations' },
  { to: '/company/settings', iconName: 'settings-2', labelKey: 'company.tabs.settings', defaultLabel: 'Company settings' },
];

function CompanyNav({ mobile = false }: { mobile?: boolean }) {
  const { t } = useTranslation();
  return (
    <nav
      className={cn('flex', mobile ? 'gap-2 overflow-x-auto pb-1' : 'flex-col gap-1 px-3 py-2')}
      aria-label={t('company.title', { defaultValue: 'Company panel' })}
    >
      {NAV_ITEMS.map((item) => (
        <NavLink
          key={item.to}
          to={item.to}
          end={item.end}
          className={({ isActive }) =>
            cn(
              'group relative inline-flex items-center gap-3 font-sans text-[14px] transition-all duration-200',
              mobile
                ? 'min-w-max rounded-md border px-4 py-2'
                : 'h-11 rounded-md px-3',
              isActive
                ? mobile
                  ? 'border-ig-accent bg-ig-accent-subtle font-medium text-ig-accent'
                  : 'bg-ig-accent text-[var(--ig-accent-text)] font-medium shadow-[0_4px_16px_-4px_var(--ig-accent-border)]'
                : mobile
                  ? 'border-ig-border bg-ig-bg-secondary text-ig-text-secondary hover:border-ig-border-strong hover:text-ig-text-primary'
                  : 'text-ig-text-secondary hover:bg-ig-bg-tertiary hover:text-ig-text-primary',
            )
          }
        >
          {({ isActive }) => (
            <>
              <span
                className={cn(
                  'flex h-7 w-7 shrink-0 items-center justify-center rounded-md transition-colors',
                  !mobile && (isActive ? 'bg-black/10' : 'bg-ig-bg-tertiary text-ig-text-tertiary group-hover:text-ig-text-secondary'),
                )}
              >
                <Icon name={item.iconName} className="h-4 w-4" />
              </span>
              <span>{t(item.labelKey, { defaultValue: item.defaultLabel })}</span>
              {!mobile && isActive ? (
                <Icon name="chevron-right" className="ml-auto h-3.5 w-3.5 opacity-70" />
              ) : null}
            </>
          )}
        </NavLink>
      ))}
    </nav>
  );
}

/** Sidebar layout for the company panel — a distinct, premium workspace shell. */
export function CompanyLayout() {
  const { t } = useTranslation();
  const user = useAuthStore((s) => s.user);
  const [searchParams, setSearchParams] = useSearchParams();

  // Stripe drops the buyer back here with ?payment=success / ?cancelled=true
  // (see the checkout returnTo whitelist on the API). Acknowledge and strip
  // the param so refreshes don't re-toast.
  useEffect(() => {
    const paid = searchParams.get('payment') === 'success';
    const cancelled = searchParams.get('cancelled') === 'true';
    if (!paid && !cancelled) return;
    if (paid) {
      toast.success(
        t('company.checkoutSuccess', {
          defaultValue: 'Payment received — your company plan is being activated. It may take a few seconds to appear.',
        }),
      );
    } else {
      toast.info(t('company.checkoutCancelled', { defaultValue: 'Checkout cancelled.' }));
    }
    const next = new URLSearchParams(searchParams);
    next.delete('payment');
    next.delete('cancelled');
    setSearchParams(next, { replace: true });
  }, [searchParams, setSearchParams, t]);

  const initials = (user?.companyName || user?.name || user?.email || '?')
    .trim()
    .split(/\s+/)
    .map((part) => part[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();

  return (
    <div className="mx-auto flex min-h-[calc(100vh-64px)] w-full max-w-[1280px]">
      <aside className="hidden w-[272px] shrink-0 flex-col border-r border-ig-border bg-ig-surface-sidebar md:flex">
        {/* Brand strip — subtle accent glow to mark this as its own workspace */}
        <div className="relative overflow-hidden border-b border-ig-border p-6">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-ig-accent opacity-[0.10] blur-2xl"
          />
          <div className="relative flex items-center gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-ig-accent font-sans text-[14px] font-semibold text-[var(--ig-accent-text)] shadow-[0_6px_18px_-4px_var(--ig-accent-border)]">
              {initials}
            </div>
            <div className="min-w-0">
              <p className="truncate font-sans text-[14px] font-semibold text-ig-text-primary">
                {user?.companyName || user?.name || t('company.title', { defaultValue: 'Company panel' })}
              </p>
              <p className="truncate font-sans text-[12px] text-ig-text-tertiary">{user?.email ?? ''}</p>
            </div>
          </div>
          <span className="relative mt-4 inline-flex items-center gap-1.5 rounded-sm bg-ig-accent-subtle px-2 py-1 font-sans text-[10px] font-semibold uppercase tracking-[0.1em] text-ig-accent">
            <Icon name="briefcase-business" className="h-3 w-3" />
            {t('company.title', { defaultValue: 'Company panel' })}
          </span>
        </div>

        <CompanyNav />

        <div className="mt-auto border-t border-ig-border p-4">
          <Link
            to="/dashboard"
            className="inline-flex w-full items-center gap-2 rounded-md px-3 py-2.5 font-sans text-[13px] text-ig-text-tertiary transition hover:bg-ig-bg-tertiary hover:text-ig-text-primary"
          >
            <Icon name="arrow-left" className="h-4 w-4" />
            {t('nav.dashboard')}
          </Link>
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <main className="flex min-w-0 flex-1 flex-col bg-ig-bg-primary px-4 py-8 md:px-10 md:py-10">
          <div className="md:hidden">
            <CompanyNav mobile />
          </div>
          <div className="mt-4 w-full md:mt-0">
            <Outlet />
          </div>
        </main>
      </div>
    </div>
  );
}
